import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit.js';
import posthog from '@/composables/usePostHog.js';
import apiClient from '@/util/apiClient';
import { stores } from '@/util/stores.js';
import type { MatchRequestAcceptResult, MatchRequestFetchOptions, MatchRequestLookup, MatchRequestRecord, MatchRequestSaveResult, MatchRequestState, MatchPositionKey, NumericLike } from '@/types/assigning';
import { statusForAssignedOfficial } from '@/util/matchAssignmentStatus';

type MatchRequestContext = Record<string, unknown> & {
	matchNumber?: NumericLike;
	visibility?: string | null;
	requestedPosition?: string | null;
	removeRequestAfterAccept?: boolean;
};

type MatchPositionEnum = 'CENTER' | 'AR1' | 'AR2' | 'FOURTH' | 'MENTOR';

interface DeleteRequestResult {
	ok: boolean;
	inFlight?: boolean;
	message?: string;
	error?: unknown;
}

type RequestListResponse = MatchRequestRecord[] | { requests?: MatchRequestRecord[]; data?: MatchRequestRecord[] };
type SaveRequestResponse = MatchRequestRecord | { data?: MatchRequestRecord };
type DeleteRequestResponse = boolean | { data?: boolean } | '' | null | undefined;

function toPathSegment(value: NumericLike): string {
	return String(value ?? '');
}

function extractRequestArray(payload: RequestListResponse | undefined): MatchRequestRecord[] {
	if (Array.isArray(payload)) return payload;
	if (payload && typeof payload === 'object') {
		if (Array.isArray(payload.requests)) return payload.requests;
		if (Array.isArray(payload.data)) return payload.data;
	}
	return [];
}

function isWrappedSaveRequest(payload: SaveRequestResponse): payload is { data?: MatchRequestRecord } {
	return Object.prototype.hasOwnProperty.call(payload, 'data');
}

function extractSavedRequest(payload: SaveRequestResponse, fallback: MatchRequestRecord): MatchRequestRecord {
	if (isWrappedSaveRequest(payload) && payload.data) {
		return payload.data;
	}
	return isWrappedSaveRequest(payload) ? fallback : payload;
}

function getErrorMessage(error: unknown, fallback: string): string {
	if (error && typeof error === 'object') {
		const response = 'response' in error ? (error as { response?: { data?: { error?: string; message?: string } } }).response : undefined;
		return response?.data?.error || response?.data?.message || ('message' in error && typeof (error as { message?: unknown }).message === 'string' ? (error as { message: string }).message : fallback);
	}
	return fallback;
}

export const useMatchRequestStore = defineStore('matchRequestStore', {
	state: (): MatchRequestState => ({
		countsByMatch: {},
		listsByMatch: {},
		loadedByMatch: {},
		prefetching: false,
		savingByMatch: {},
		deletingByRequestNumber: {}
	}),
	getters: {
		getCount: (state) => (matchKey: string) => state.countsByMatch[matchKey] ?? 0,
		getList: (state) => (matchKey: string) => state.listsByMatch[matchKey] ?? [],
		isLoaded: (state) => (matchKey: string) => !!state.loadedByMatch[matchKey],
		isSavingMatch: (state) => (requestMatchKey: string) => !!state.savingByMatch[requestMatchKey],
		isDeletingRequest: (state) => (requestNumber: NumericLike) => !!state.deletingByRequestNumber[Number(requestNumber)]
	},
	actions: {
		makeMatchKey(groupNumber: NumericLike, groupMatchNumber: NumericLike): string | null {
			const g = Number(groupNumber);
			const m = Number(groupMatchNumber);
			if (!Number.isFinite(g) || !Number.isFinite(m)) return null;
			return `${String(g)}:${String(m)}`;
		},

		getMatchKeyFromRequest(request: MatchRequestRecord | null | undefined): string | null {
			const groupNumber = request?.group?.groupNumber ?? request?.requestedGroup?.groupNumber ?? request?.groupNumber ?? request?.requestedGroupNumber ?? request?.match?.group?.groupNumber ?? request?.requestedMatch?.group?.groupNumber;

			const groupMatchNumber = request?.requestedMatchNumber ?? request?.requestedGroupMatchNumber ?? request?.groupMatchNumber ?? request?.match?.groupMatchNumber ?? request?.requestedMatch?.groupMatchNumber;

			return this.makeMatchKey(groupNumber, groupMatchNumber);
		},

		async fetchPendingRequests(matches: Array<MatchRequestLookup | MatchRequestRecord> = [], requestingUserNumber: NumericLike = null): Promise<MatchRequestRecord[]> {
			const visibleMatchKeys = new Set<string>();
			for (const match of matches) {
				const record = match as MatchRequestRecord;
				const matchKey = this.makeMatchKey(record.group?.groupNumber ?? record.groupNumber, record.groupMatchNumber);
				if (matchKey) visibleMatchKeys.add(matchKey);
			}

			if (!visibleMatchKeys.size) return [];
			this.prefetching = true;

			try {
				// apiClient already includes /api/v1 in its base URL.
				const response = await apiClient.get<RequestListResponse>('/request/pending');
				const requests = extractRequestArray(response.data).map((request) => this.normalizeRequest(request, { requestingUserNumber }));
				const byMatch: Record<string, MatchRequestRecord[]> = {};

				for (const request of requests) {
					const matchKey = this.getMatchKeyFromRequest(request);
					if (!matchKey) continue;
					const list = byMatch[matchKey] ?? [];
					list.push(request);
					byMatch[matchKey] = list;
				}

				for (const matchKey of visibleMatchKeys) {
					const list = byMatch[matchKey] ?? [];
					this.listsByMatch[matchKey] = list;
					this.countsByMatch[matchKey] = list.length;
					this.loadedByMatch[matchKey] = true;
				}

				return requests;
			} catch {
				return [];
			} finally {
				this.prefetching = false;
			}
		},

		async fetchRequestsForClient(clientIds: NumericLike[] = [], opts: MatchRequestFetchOptions = {}): Promise<MatchRequestRecord[]> {
			if (shouldThrottle('fetchRequestsForClient', 100)) {
				posthog.capture('throttled_action', { action: 'fetchRequestsForClient' });
				return [];
			}
			if (!clientIds.length) return [];

			this.prefetching = true;

			try {
				const response = await apiClient.post<RequestListResponse>('/request/client/requests', clientIds);
				const requests = extractRequestArray(response.data);
				const visible = Array.isArray(opts.visibleMatchKeys) ? opts.visibleMatchKeys.filter(Boolean) : [];
				const allow = visible.length ? new Set(visible) : null;
				const byMatch: Record<string, MatchRequestRecord[]> = {};
				// Visible matches omitted from the response no longer have requests.
				for (const matchKey of visible) byMatch[matchKey] = [];

				for (const request of requests) {
					const matchKey = this.getMatchKeyFromRequest(request);
					if (!matchKey) continue;
					if (allow?.has(matchKey) === false) continue;
					const list = byMatch[matchKey] ?? [];
					list.push(request);
					byMatch[matchKey] = list;
				}

				for (const [matchKey, list] of Object.entries(byMatch)) {
					this.listsByMatch[matchKey] = list;
					this.countsByMatch[matchKey] = list.length;
					this.loadedByMatch[matchKey] = true;
				}

				return requests;
			} catch {
				return [];
			} finally {
				this.prefetching = false;
			}
		},

		async ensureRequests(matchLookup: MatchRequestLookup, opts: { mode?: string } = {}): Promise<MatchRequestRecord[]> {
			const groupNumber = Number(matchLookup.groupNumber);
			const groupMatchNumber = Number(matchLookup.groupMatchNumber);
			const matchKey = this.makeMatchKey(groupNumber, groupMatchNumber);
			if (!matchKey) return [];

			if (opts.mode !== 'refresh' && this.loadedByMatch[matchKey]) {
				return this.listsByMatch[matchKey] ?? [];
			}

			try {
				const response = await apiClient.get<RequestListResponse>(`/request/group/${toPathSegment(groupNumber)}/match/${toPathSegment(groupMatchNumber)}`);
				const requests = extractRequestArray(response.data);
				this.listsByMatch[matchKey] = requests;
				this.countsByMatch[matchKey] = requests.length;
				this.loadedByMatch[matchKey] = true;
				return requests;
			} catch {
				this.listsByMatch[matchKey] = [];
				this.countsByMatch[matchKey] = 0;
				this.loadedByMatch[matchKey] = true;
				return [];
			}
		},

		markVisibleMatchesLoaded(matches: Array<string | MatchRequestLookup | MatchRequestRecord> = []) {
			for (const match of matches) {
				const groupNumber = typeof match === 'string' ? null : 'group' in match ? match.group?.groupNumber : match.groupNumber;
				const groupMatchNumber = typeof match === 'string' ? null : match.groupMatchNumber;
				const key = typeof match === 'string' ? match : this.makeMatchKey(groupNumber, groupMatchNumber);
				if (!key) continue;
				this.loadedByMatch[key] = true;
				if (this.countsByMatch[key] == null) this.countsByMatch[key] = 0;
				if (!this.listsByMatch[key]) this.listsByMatch[key] = [];
			}
		},

		async saveRequest(request: MatchRequestRecord): Promise<MatchRequestSaveResult> {
			const requestMatchKey = this.getRequestMatchKeyFromRequest(request);
			const groupNumber = request.group?.groupNumber ?? request.requestedGroup?.groupNumber ?? request.groupNumber ?? request.requestedGroupNumber ?? request.match?.group?.groupNumber ?? request.requestedMatch?.group?.groupNumber;
			const groupMatchNumber = request.requestedMatchNumber ?? request.requestedGroupMatchNumber ?? request.groupMatchNumber ?? request.match?.groupMatchNumber ?? request.requestedMatch?.groupMatchNumber;
			const userNumber = request.user?.userNumber ?? request.requestingUserNumber ?? request.userNumber;

			if (this.hasRequestForMatch(groupNumber, groupMatchNumber, userNumber)) {
				return {
					ok: true,
					duplicate: true,
					message: 'You already requested this match'
				};
			}

			if (requestMatchKey && this.savingByMatch[requestMatchKey]) {
				return {
					ok: false,
					duplicate: false,
					inFlight: true,
					message: 'A request for this match is already being submitted'
				};
			}

			if (shouldThrottle('saveMatchRequest', 100)) {
				posthog.capture('throttled_action', { action: 'saveMatchRequest' });
				return { ok: false };
			}
			if (requestMatchKey) this.savingByMatch[requestMatchKey] = true;

			try {
				const response = await apiClient.post<SaveRequestResponse>('/request/save', request);
				const saved = this.normalizeRequest(extractSavedRequest(response.data, request), request);
				const matchKey = this.getMatchKeyFromRequest(saved);
				if (matchKey) {
					const current = this.listsByMatch[matchKey] ?? [];
					const alreadyExists = current.some((entry) => {
						const currentUser = Number(entry.requestingUser?.userNumber ?? entry.user?.userNumber ?? entry.requestingUserNumber ?? entry.userNumber);
						return currentUser === Number(saved.requestingUserNumber);
					});
					if (!alreadyExists) {
						this.listsByMatch[matchKey] = [...current, saved];
						this.countsByMatch[matchKey] = this.listsByMatch[matchKey].length;
						this.loadedByMatch[matchKey] = true;
					}
				}
				return {
					ok: true,
					duplicate: false,
					data: saved
				};
			} catch (error) {
				const apiError = getErrorMessage(error, 'Failed to save request');
				const isDuplicate = apiError.toLowerCase().includes('request already exists');
				if (isDuplicate) {
					const synthetic = this.normalizeRequest(undefined, request);
					const matchKey = this.getMatchKeyFromRequest(synthetic);
					if (matchKey) {
						const current = this.listsByMatch[matchKey] ?? [];
						const alreadyExists = current.some((entry) => {
							const currentUser = Number(entry.requestingUser?.userNumber ?? entry.user?.userNumber ?? entry.requestingUserNumber ?? entry.userNumber);
							return currentUser === Number(synthetic.requestingUserNumber);
						});
						if (!alreadyExists) {
							this.listsByMatch[matchKey] = [...current, synthetic];
							this.countsByMatch[matchKey] = this.listsByMatch[matchKey].length;
							this.loadedByMatch[matchKey] = true;
						}
					}
					return {
						ok: true,
						duplicate: true,
						message: 'Request already exists'
					};
				}
				return {
					ok: false,
					duplicate: false,
					message: apiError,
					error
				};
			} finally {
				if (requestMatchKey) this.savingByMatch[requestMatchKey] = false;
			}
		},

		findCachedRequestByNumber(requestNumber: NumericLike): MatchRequestRecord | null {
			const key = Number(requestNumber);
			if (!Number.isFinite(key)) return null;

			for (const list of Object.values(this.listsByMatch)) {
				const found = list.find((request) => Number(request.requestNumber ?? request.request_number) === key);
				if (found) return found;
			}
			return null;
		},

		async deleteRequest(requestOrNumber: MatchRequestRecord | NumericLike): Promise<DeleteRequestResult> {
			const request = typeof requestOrNumber === 'object' && requestOrNumber !== null ? requestOrNumber : null;
			const key = Number(request?.requestNumber ?? request?.request_number ?? requestOrNumber);
			if (!Number.isFinite(key)) {
				return {
					ok: false,
					message: 'Invalid request number'
				};
			}
			if (this.deletingByRequestNumber[key]) {
				return {
					ok: false,
					inFlight: true,
					message: 'Request is already being deleted'
				};
			}

			const cachedRequest = request ?? this.findCachedRequestByNumber(key);
			const matchKey = cachedRequest ? this.getMatchKeyFromRequest(cachedRequest) : null;
			const previousList = matchKey ? [...(this.listsByMatch[matchKey] ?? [])] : null;

			if (matchKey) {
				const nextList = (previousList ?? []).filter((entry) => Number(entry.requestNumber ?? entry.request_number) !== key);
				this.listsByMatch[matchKey] = nextList;
				this.countsByMatch[matchKey] = nextList.length;
				this.loadedByMatch[matchKey] = true;
			}

			this.deletingByRequestNumber[key] = true;

			try {
				const response = await apiClient.delete<DeleteRequestResponse>(`/request/delete/${toPathSegment(key)}`);
				// Assignment can already have removed a fulfilled request. The API
				// returns 204 for that successful, idempotent cleanup.
				const deleted = response.status === 204 || response.data === true || (typeof response.data === 'object' && response.data?.data === true);
				if (!deleted) {
					if (matchKey && previousList) {
						this.listsByMatch[matchKey] = previousList;
						this.countsByMatch[matchKey] = previousList.length;
						this.loadedByMatch[matchKey] = true;
					}
					return {
						ok: false,
						message: 'Request was not deleted'
					};
				}
				return { ok: true };
			} catch (error) {
				if (matchKey && previousList) {
					this.listsByMatch[matchKey] = previousList;
					this.countsByMatch[matchKey] = previousList.length;
					this.loadedByMatch[matchKey] = true;
				}
				return {
					ok: false,
					message: getErrorMessage(error, 'Failed to delete request'),
					error
				};
			} finally {
				this.deletingByRequestNumber = Object.fromEntries(Object.entries(this.deletingByRequestNumber).filter(([requestNumber]) => Number(requestNumber) !== key));
			}
		},

		makeRequestMatchKey(groupNumber: NumericLike, groupMatchNumber: NumericLike, userNumber: NumericLike): string | null {
			const g = Number(groupNumber);
			const m = Number(groupMatchNumber);
			const u = Number(userNumber);
			if (!Number.isFinite(g) || !Number.isFinite(m) || !Number.isFinite(u)) return null;
			return `${String(g)}:${String(m)}:${String(u)}`;
		},

		getRequestMatchKeyFromRequest(request: MatchRequestRecord | null | undefined): string | null {
			const groupNumber = request?.group?.groupNumber ?? request?.requestedGroup?.groupNumber ?? request?.groupNumber ?? request?.requestedGroupNumber ?? request?.match?.group?.groupNumber ?? request?.requestedMatch?.group?.groupNumber;

			const groupMatchNumber = request?.requestedMatchNumber ?? request?.requestedGroupMatchNumber ?? request?.groupMatchNumber ?? request?.match?.groupMatchNumber ?? request?.requestedMatch?.groupMatchNumber;

			const userNumber = request?.user?.userNumber ?? request?.requestingUserNumber ?? request?.userNumber;
			return this.makeRequestMatchKey(groupNumber, groupMatchNumber, userNumber);
		},

		removeCachedRequest(requestOrNumber: MatchRequestRecord | NumericLike): boolean {
			const request = typeof requestOrNumber === 'object' && requestOrNumber !== null ? requestOrNumber : this.findCachedRequestByNumber(requestOrNumber);
			if (!request) return false;

			const requestNumber = Number(request.requestNumber ?? request.request_number ?? requestOrNumber);
			const matchKey = this.getMatchKeyFromRequest(request);
			if (!Number.isFinite(requestNumber) || !matchKey) return false;

			const previousList = this.listsByMatch[matchKey] ?? [];
			const nextList = previousList.filter((entry) => Number(entry.requestNumber ?? entry.request_number) !== requestNumber);
			this.listsByMatch[matchKey] = nextList;
			this.countsByMatch[matchKey] = nextList.length;
			this.loadedByMatch[matchKey] = true;
			return true;
		},

		resolvePositionKey(requestedPosition: string | null | undefined): MatchPositionKey | null {
			const normalized = (requestedPosition ?? '').trim().toUpperCase();

			switch (normalized) {
				case 'CR':
				case 'CENTER':
					return 'center';
				case 'AR1':
					return 'ar1';
				case 'AR2':
					return 'ar2';
				case 'FOURTH':
				case '4TH':
					return 'fourth';
				case 'MENTOR':
					return 'mentor';
				default:
					return null;
			}
		},

		resolvePositionEnum(requestedPosition: string | null | undefined): MatchPositionEnum | null {
			const normalized = (requestedPosition ?? '').trim().toUpperCase();

			switch (normalized) {
				case 'CR':
				case 'CENTER':
					return 'CENTER';
				case 'AR1':
					return 'AR1';
				case 'AR2':
					return 'AR2';
				case 'FOURTH':
				case '4TH':
					return 'FOURTH';
				case 'MENTOR':
					return 'MENTOR';
				default:
					return null;
			}
		},

		async acceptRequest(request: MatchRequestRecord, context: MatchRequestContext = {}): Promise<MatchRequestAcceptResult> {
			const groupNumber = request.group?.groupNumber ?? request.requestedGroup?.groupNumber ?? request.requestedMatch?.group?.groupNumber ?? request.groupNumber;
			const groupMatchNumber = request.requestedMatchNumber ?? request.requestedMatch?.groupMatchNumber ?? request.groupMatchNumber;
			// An assignor may place a request in another open position. Keep the
			// original request intact, while using the selected position for this
			// assignment.
			const positionKey = this.resolvePositionKey(context.requestedPosition ?? request.requestedPosition);
			const requestingUserNumber = Number(request.requestingUser?.userNumber ?? request.user?.userNumber ?? request.requestingUserNumber ?? request.userNumber);
			let matchNumber = Number(context.matchNumber ?? request.requestedMatch?.matchNumber ?? request.match?.matchNumber);
			let matchVisibility = context.visibility;
			if (!positionKey || !Number.isFinite(Number(groupNumber)) || !Number.isFinite(Number(groupMatchNumber)) || !Number.isFinite(requestingUserNumber) || requestingUserNumber <= 0) {
				return {
					ok: false,
					message: 'Request is missing required match assignment data'
				};
			}
			try {
				if (!Number.isFinite(matchNumber)) {
					const match = await stores.matchStore.fetchMatchByGroupAndGroupMatchNumber(groupNumber, groupMatchNumber);

					matchNumber = Number(match?.matchNumber);
					matchVisibility = match?.visibility ?? matchVisibility;

					if (!Number.isFinite(matchNumber)) {
						return {
							ok: false,
							message: 'Unable to load the requested match'
						};
					}
				}
				const positionPatchFieldByKey: Record<MatchPositionKey, string> = {
					center: 'centerId',
					ar1: 'ar1Id',
					ar2: 'ar2Id',
					fourth: 'fourthId',
					mentor: 'mentorId'
				};
				const statusPatchFieldByKey: Record<MatchPositionKey, string> = {
					center: 'crStatus',
					ar1: 'ar1Status',
					ar2: 'ar2Status',
					fourth: 'fourthStatus',
					mentor: 'mentorStatus'
				};
				const statusField = statusPatchFieldByKey[positionKey];
				const assignedStatus = statusForAssignedOfficial(matchVisibility);
				const patchPayload: Record<string, unknown> = {
					[positionPatchFieldByKey[positionKey]]: requestingUserNumber,
					[statusField]: assignedStatus
				};
				const updatedMatch = await stores.matchStore.updateMatchByGroupNumber(groupNumber, groupMatchNumber, patchPayload);
				// A skipped or incomplete update must never consume the request.
				if (!updatedMatch || Number(updatedMatch[positionKey]?.userNumber) !== requestingUserNumber) {
					return {
						ok: false,
						message: 'The match update did not confirm this official in the selected position. The request was not removed. Refresh the match and try again.'
					};
				}
				const result: MatchRequestAcceptResult = {
					ok: true,
					matchNumber,
					groupNumber: Number(groupNumber),
					groupMatchNumber: Number(groupMatchNumber),
					positionKey,
					assignedUser: updatedMatch[positionKey] ?? null,
					assignedStatus: typeof updatedMatch[statusField] === 'string' ? updatedMatch[statusField] : assignedStatus
				};
				if (context.removeRequestAfterAccept) {
					const deleteResult = await this.deleteRequest(request);
					if (!deleteResult.ok) {
						// Same-position assignment can remove the request before DELETE.
						// Reconcile an ambiguous failure with the server rather than
						// restoring a fulfilled request indefinitely in the local list.
						let removalConfirmed = false;
						try {
							const response = await apiClient.get<RequestListResponse | null | undefined>(`/request/group/${toPathSegment(groupNumber)}/match/${toPathSegment(groupMatchNumber)}`);
							const requests = response.status === 204 ? [] : Array.isArray(response.data) ? response.data : (response.data?.requests ?? response.data?.data);
							const matchKey = this.makeMatchKey(groupNumber, groupMatchNumber);
							if (Array.isArray(requests) && matchKey) {
								this.listsByMatch[matchKey] = requests;
								this.countsByMatch[matchKey] = requests.length;
								this.loadedByMatch[matchKey] = true;
								removalConfirmed = !requests.some((entry) => Number(entry.requestNumber ?? entry.request_number) === Number(request.requestNumber ?? request.request_number));
							}
						} catch {
							// An unsuccessful read is not evidence that the request is gone.
						}
						if (!removalConfirmed) {
							result.requestRemovalWarning = `The official was assigned, but the request could not be removed: ${deleteResult.message || 'Unknown error'}`;
						}
					}
				}
				return result;
			} catch (error) {
				return {
					ok: false,
					message: getErrorMessage(error, 'Failed to accept request'),
					error
				};
			}
		},

		hasRequestForMatch(groupNumber: NumericLike, groupMatchNumber: NumericLike, userNumber: NumericLike): boolean {
			const matchKey = this.makeMatchKey(groupNumber, groupMatchNumber);
			const normalizedUserNumber = Number(userNumber);
			if (!matchKey || !Number.isFinite(normalizedUserNumber)) return false;

			return (this.listsByMatch[matchKey] ?? []).some((entry) => {
				const requestUserNumber = Number(entry.requestingUser?.userNumber ?? entry.user?.userNumber ?? entry.requestingUserNumber ?? entry.userNumber);
				return requestUserNumber === normalizedUserNumber;
			});
		},

		hasPendingRequestForMatch(groupNumber: NumericLike, groupMatchNumber: NumericLike, userNumber: NumericLike): boolean {
			const requestMatchKey = this.makeRequestMatchKey(groupNumber, groupMatchNumber, userNumber);
			return requestMatchKey ? !!this.savingByMatch[requestMatchKey] : false;
		},

		normalizeRequest(request: MatchRequestRecord | null | undefined, fallback: MatchRequestRecord = {}): MatchRequestRecord {
			const source = request ?? {};
			const groupNumber = source.group?.groupNumber ?? source.requestedGroup?.groupNumber ?? source.groupNumber ?? fallback.groupNumber ?? null;
			const groupMatchNumber = source.requestedMatchNumber ?? source.requestedGroupMatchNumber ?? source.groupMatchNumber ?? fallback.requestedMatchNumber ?? null;
			const requestedPosition = (source.requestedPosition ?? source.position ?? source.slot ?? fallback.requestedPosition ?? '').toUpperCase();
			const requestingUserNumber = Number(source.requestingUser?.userNumber ?? source.user?.userNumber ?? source.requestingUserNumber ?? source.userNumber ?? fallback.requestingUserNumber ?? fallback.userNumber);
			return {
				...source,
				group: source.group ?? { groupNumber: Number(groupNumber) },
				requestedMatchNumber: Number(groupMatchNumber),
				requestedPosition,
				requestingUserNumber: Number.isFinite(requestingUserNumber) ? requestingUserNumber : null
			};
		}
	}
});
